import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { environment } from '../../../environments/environment.development';
import { BrandingService } from './branding.service';
import { ConsentService } from './consent.service';
import { hasSensitiveUrlParam } from './sensitive-url-params';

/**
 * CapyStats public site key format: `pk_live_` + url-safe base64 chars.
 * We NEVER inject a key that fails this regex (defence against config typos /
 * injection), mirroring the GA4/Meta id discipline in {@link AnalyticsService}.
 */
const CAPYSTATS_KEY_RE = /^pk_live_[A-Za-z0-9_-]+$/;

/** Official cookieless tracker + opt-in auto events. */
const TRACKER_SRC = 'https://t.capystats.com/a.js';
/** engagement = active time on page, plus scroll depth, outbound + downloads. */
const AUTO_EVENTS = 'engagement,scroll,outbound,downloads';

/** Prop values are restricted to primitive scalars; nothing else is ever sent. */
export type CapyStatsPropValue = string | number | boolean;
export type CapyStatsProps = Record<string, CapyStatsPropValue>;

/** Minimal typing for the global the `/a.js` snippet creates. */
interface CapyAnalytics {
  track(name: string, props?: Record<string, string | number | boolean>): void;
  pageview?(): void;
}
interface CapyWindow extends Window {
  analytics?: CapyAnalytics;
}

/**
 * Exact prop-key names (case-insensitive) that must never carry data.
 * Defence-in-depth: callers already pass only safe labels.
 *
 * EXACT match (not substring) on purpose — so the meta-flags `hasName` /
 * `hasMessage` and the public catalog labels `propertyName` / `typeName` /
 * `displayName` survive, while a careless `{ name }` / `{ email }` is dropped.
 */
const PII_KEYS = new Set([
  'email', 'e-mail', 'mail', 'phone', 'tel', 'telephone', 'mobile', 'whatsapp',
  'name', 'firstname', 'first_name', 'lastname', 'last_name', 'fullname',
  'full_name', 'surname', 'guestname', 'guestemail', 'guestphone', 'username',
  'useremail', 'message', 'msg', 'note', 'address', 'street', 'zip', 'postcode',
  'postal', 'iban', 'nip', 'pesel', 'password', 'passwd', 'secret', 'token',
]);

/**
 * Cookieless visitor analytics (CapyStats) for the public app.
 *
 * Injection rules (cookieless, so no consent banner is required for CapyStats
 * itself — only DNT / GPC are honoured):
 *   • Main host (zaklepuj.pl) — inject the environment site key.
 *   • White-label host with ONLY CapyStats — no consent banner is shown, so
 *     inject the owner's per-domain key (`branding.analytics().capyStatsSiteKey`)
 *     straight away.
 *   • White-label host that ALSO runs cookie-based tags (GA4 / Meta / GTM) — a
 *     consent banner IS shown for those, so we defer to its analytics choice
 *     and only inject once `consent.analyticsGranted()` is true, keeping the
 *     banner honest.
 * DNT / GPC always wins, regardless of the above.
 *
 * `/a.js` auto-tracks SPA route changes, engagement time, scroll, outbound and
 * downloads, so no manual page_view is dispatched (that would double-count).
 * Custom conversion events go through {@link track}.
 *
 * Token-leak ban: `/a.js` sends `location.href` with the load pageview and every
 * event and keeps it for engagement events. While the URL carries a
 * credential-like param (`t` / `token`, see `sensitive-url-params.ts`) the
 * tracker is NOT injected (re-checked on every NavigationEnd) and `track()` is a
 * no-op. `main.ts` strips such params before the bootstrap, so this is defence in depth.
 */
@Injectable({ providedIn: 'root' })
export class CapyStatsService {
  private readonly doc = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly branding = inject(BrandingService);
  private readonly consent = inject(ConsentService);

  private injected = false;
  /** Key waiting for a credential-free URL (null = nothing pending). */
  private pendingKey: string | null = null;
  private routerHooked = false;

  constructor() {
    // Re-evaluate whenever white-label state or analytics consent changes.
    effect(() => {
      const whiteLabel = this.branding.isWhiteLabel();
      const hasCookieTags = this.branding.hasCookieAnalytics();
      const consentOk = this.consent.analyticsGranted();
      const key = this.resolveKey(whiteLabel);
      if (!key) {
        this.pendingKey = null;
        return;
      }
      // CapyStats is cookieless, so it needs no banner of its own. We only defer
      // to consent on white-label hosts that ALSO run cookie-based tags (those
      // do show a banner) — otherwise inject straight away (DNT/GPC still apply).
      if (whiteLabel && hasCookieTags && !consentOk) {
        this.pendingKey = null;
        return;
      }
      this.requestInject(key);
    });
  }

  /** Injects now, or — while the URL carries a credential — once a navigation cleans it. */
  private requestInject(key: string): void {
    if (this.injected) {
      return;
    }
    if (this.urlHasCredential()) {
      this.pendingKey = key;
      this.watchNavigation();
      return;
    }
    this.pendingKey = null;
    this.inject(key);
  }

  private watchNavigation(): void {
    if (this.routerHooked) {
      return;
    }
    this.routerHooked = true;
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        const key = this.pendingKey;
        if (key) {
          this.requestInject(key);
        }
      });
  }

  /** `t=` / `token=` in the current address bar (read live — the tracker reads it live too). */
  private urlHasCredential(): boolean {
    return hasSensitiveUrlParam(this.doc.defaultView?.location?.search);
  }

  private resolveKey(whiteLabel: boolean): string | null {
    const raw = whiteLabel
      ? this.branding.analytics()?.capyStatsSiteKey
      : environment.capyStatsSiteKey;
    const key = raw?.trim();
    return key && CAPYSTATS_KEY_RE.test(key) ? key : null;
  }

  private inject(key: string): void {
    if (this.injected) {
      return;
    }
    const win = this.doc.defaultView as CapyWindow | null;
    if (!win || this.dntOrGpc(win)) {
      return;
    }
    const script = this.doc.createElement('script');
    script.defer = true;
    script.src = TRACKER_SRC;
    script.setAttribute('data-site', key);
    script.setAttribute('data-events', AUTO_EVENTS);
    this.doc.head.appendChild(script);
    this.injected = true;
  }

  /** Honour Do-Not-Track and Global Privacy Control even when consent is given. */
  private dntOrGpc(win: Window): boolean {
    const nav = win.navigator as Navigator & {
      globalPrivacyControl?: boolean;
      msDoNotTrack?: string;
    };
    const dntWin = (win as Window & { doNotTrack?: string }).doNotTrack;
    return (
      nav?.doNotTrack === '1' ||
      dntWin === '1' ||
      nav?.msDoNotTrack === '1' ||
      nav?.globalPrivacyControl === true
    );
  }

  /**
   * Fire a custom event. No-ops silently when the tracker is not loaded yet
   * (not injected / consent withheld / DNT) or while the URL carries a
   * credential (the event would carry `location.href`). Props are sanitised:
   * only finite scalars survive, strings are clamped to 500 chars, max 30 keys,
   * and any PII-shaped key is dropped.
   */
  track(name: string, props?: CapyStatsProps): void {
    const win = this.doc.defaultView as CapyWindow | null;
    const fn = win?.analytics?.track;
    if (typeof fn !== 'function' || this.urlHasCredential()) {
      return;
    }
    try {
      fn(name, props ? this.sanitize(props) : undefined);
    } catch {
      // Analytics must never break the app.
    }
  }

  private sanitize(props: CapyStatsProps): CapyStatsProps {
    const out: CapyStatsProps = {};
    let count = 0;
    for (const [k, v] of Object.entries(props)) {
      if (count >= 30) {
        break;
      }
      if (PII_KEYS.has(k.toLowerCase()) || v === null || v === undefined) {
        continue;
      }
      if (typeof v === 'string') {
        // Any '@' means it looks like an email — never send, whatever the key.
        if (v.includes('@')) {
          continue;
        }
        // `query` is the only prop carrying raw user input; drop it when it looks
        // like a phone number (7+ digits) so a typed name + phone never lands here.
        if (k.toLowerCase() === 'query' && v.replace(/\D/g, '').length >= 7) {
          continue;
        }
        const s = v.slice(0, 500);
        if (s.length) {
          out[k] = s;
          count++;
        }
      } else if (typeof v === 'number' && Number.isFinite(v)) {
        out[k] = v;
        count++;
      } else if (typeof v === 'boolean') {
        out[k] = v;
        count++;
      }
    }
    return out;
  }
}
